import { osloDateParts } from "./oslo-time";

export const DEFAULT_REPORTING_RECONCILIATION_DAY = 12;

const NB_MONTH = new Intl.DateTimeFormat("nb-NO", {
  month: "long",
  timeZone: "UTC",
});

const NB_MONTH_YEAR = new Intl.DateTimeFormat("nb-NO", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

const NB_DAY_MONTH = new Intl.DateTimeFormat("nb-NO", {
  day: "numeric",
  month: "long",
  timeZone: "UTC",
});

const NB_DAY_MONTH_YEAR = new Intl.DateTimeFormat("nb-NO", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

export interface ReportingReconciliationPeriod {
  reconciliationDay: number;
  currentYear: number;
  reconciledThroughYear: number;
  reconciledThroughMonth: number;
  reconciledThroughIso: string;
  nextPeriodYear: number;
  nextPeriodMonth: number;
  nextReconciliationIso: string;
}

function clampReconciliationDay(day: number | null | undefined): number {
  if (!Number.isFinite(day)) return DEFAULT_REPORTING_RECONCILIATION_DAY;
  return Math.min(28, Math.max(1, Math.trunc(day!)));
}

function isoDate(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function utcDate(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month - 1, day));
}

/**
 * Operativ rapporteringsperiode for Sølvrev.
 *
 * Før avstemmingsdagen er måneden før forrige måned siste avstemte periode.
 * Fra og med avstemmingsdagen er forrige måned siste avstemte periode.
 * Kalenderdatoen tolkes alltid i Europe/Oslo.
 */
export function getReportingReconciliationPeriod(
  now: Date,
  configuredDay: number | null | undefined = DEFAULT_REPORTING_RECONCILIATION_DAY,
): ReportingReconciliationPeriod {
  const reconciliationDay = clampReconciliationDay(configuredDay);
  const { year: currentYear, month: currentMonth, day: currentDay } = osloDateParts(now);
  const monthsBack = currentDay >= reconciliationDay ? 1 : 2;
  const reconciledMonthStart = new Date(Date.UTC(currentYear, currentMonth - 1 - monthsBack, 1));
  const reconciledThroughYear = reconciledMonthStart.getUTCFullYear();
  const reconciledThroughMonth = reconciledMonthStart.getUTCMonth() + 1;
  const reconciledThroughDay = new Date(
    Date.UTC(reconciledThroughYear, reconciledThroughMonth, 0),
  ).getUTCDate();

  const nextPeriodStart = new Date(
    Date.UTC(reconciledThroughYear, reconciledThroughMonth, 1),
  );
  const nextPeriodYear = nextPeriodStart.getUTCFullYear();
  const nextPeriodMonth = nextPeriodStart.getUTCMonth() + 1;
  const nextReconciliationStart = new Date(
    Date.UTC(nextPeriodYear, nextPeriodMonth, reconciliationDay),
  );

  return {
    reconciliationDay,
    currentYear,
    reconciledThroughYear,
    reconciledThroughMonth,
    reconciledThroughIso: isoDate(
      reconciledThroughYear,
      reconciledThroughMonth,
      reconciledThroughDay,
    ),
    nextPeriodYear,
    nextPeriodMonth,
    nextReconciliationIso: isoDate(
      nextReconciliationStart.getUTCFullYear(),
      nextReconciliationStart.getUTCMonth() + 1,
      nextReconciliationStart.getUTCDate(),
    ),
  };
}

export function reconciledMonthForYear(
  period: ReportingReconciliationPeriod,
  year: number,
): number {
  if (year < period.currentYear) return 12;
  if (year > period.currentYear) return 0;
  if (period.reconciledThroughYear < year) return 0;
  return period.reconciledThroughMonth;
}

export function formatReportingReconciliationStatus(
  period: ReportingReconciliationPeriod,
): string {
  const through = utcDate(
    period.reconciledThroughYear,
    period.reconciledThroughMonth,
    Number(period.reconciledThroughIso.slice(-2)),
  );
  const nextPeriod = utcDate(period.nextPeriodYear, period.nextPeriodMonth, 1);
  const nextReconciliation = new Date(`${period.nextReconciliationIso}T00:00:00Z`);
  const crossesYear = period.reconciledThroughYear !== period.currentYear;

  const throughLabel = crossesYear
    ? NB_DAY_MONTH_YEAR.format(through)
    : NB_DAY_MONTH.format(through);
  const nextPeriodLabel = NB_MONTH.format(nextPeriod);
  const nextDateLabel = crossesYear
    ? NB_DAY_MONTH_YEAR.format(nextReconciliation)
    : NB_DAY_MONTH.format(nextReconciliation);

  return `Tall per ${throughLabel} · ${nextPeriodLabel} avstemmes ${nextDateLabel}`;
}

/** Måned/år-etiketten som brukes i «til og med [måned]»-KPI-er. */
export function formatReconciledThroughPeriod(
  period: ReportingReconciliationPeriod,
): string {
  return NB_MONTH_YEAR.format(
    utcDate(period.reconciledThroughYear, period.reconciledThroughMonth, 1),
  );
}
